package at.theatervogelweide.admin.web

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import at.theatervogelweide.admin.contract.SET_PASSWORD_PATH
import at.theatervogelweide.admin.web.components.UnsavedChangesGuard
import kotlinx.browser.window
import org.w3c.dom.PopStateEvent

/** The client routes. Each has one path; a new screen adds a subclass and a case in [parse]. */
sealed class Route(val path: String) {
    data object Overview : Route("/")

    /** [returnTo] is where to go after signing in; it stays in memory, not in the address. */
    data class SignIn(val returnTo: Route? = null) : Route(PATH) {
        companion object {
            const val PATH = "/anmelden"
        }
    }

    /**
     * Neuigkeiten with the item [selectedId] in the editor, or the first one without it, or a new one with [new]; the
     * screen then replaces the address with its plain path.
     */
    data class News(val selectedId: Long? = null, val new: Boolean = false) : Route(
        when {
            new -> "$PATH/$NEW"
            selectedId != null -> "$PATH/$selectedId"
            else -> PATH
        },
    ) {
        companion object {
            const val PATH = "/neuigkeiten"
            const val NEW = "neu"
        }
    }

    /**
     * Stücke & Termine: the list, or the editor of the act [actId], or of a new one with [new]. One route for all three,
     * so the navigation marks it on each.
     */
    data class Acts(val actId: Long? = null, val new: Boolean = false) : Route(
        when {
            new -> "$PATH/$NEW"
            actId != null -> "$PATH/$actId"
            else -> PATH
        },
    ) {
        companion object {
            const val PATH = "/stuecke"
            const val NEW = "neu"
        }
    }

    /** Personen with the person [selectedId] in the editor, the first one without it, or a new person with [new]. */
    data class People(val selectedId: Long? = null, val new: Boolean = false) : Route(
        when {
            new -> "$PATH/$NEW"
            selectedId != null -> "$PATH/$selectedId"
            else -> PATH
        },
    ) {
        companion object {
            const val PATH = "/personen"
            const val NEW = "neu"
        }
    }

    /** Vorstand with the member [selectedId] in the editor, the first one without it, or a new member with [new]. */
    data class Board(val selectedId: Long? = null, val new: Boolean = false) : Route(
        when {
            new -> "$PATH/$NEW"
            selectedId != null -> "$PATH/$selectedId"
            else -> PATH
        },
    ) {
        companion object {
            const val PATH = "/vorstand"
            const val NEW = "neu"
        }
    }

    /** Sponsoren (#94) with the sponsor [selectedId] in the editor, the first one without it, or a new one with [new]. */
    data class Sponsors(val selectedId: Long? = null, val new: Boolean = false) : Route(
        when {
            new -> "$PATH/$NEW"
            selectedId != null -> "$PATH/$selectedId"
            else -> PATH
        },
    ) {
        companion object {
            const val PATH = "/sponsoren"
            const val NEW = "neu"
        }
    }

    /** Verein (#106): superusers edit the club's texts, other admins read them. */
    data object Club : Route("/verein")

    /** Impressum (#84): superusers edit it, other admins read it. */
    data object Imprint : Route("/impressum")

    /** Verbundene Anwendungen: the MCP clients admins allowed (#34). */
    data object Connections : Route("/verbindungen")

    /** Konten, for superusers (#66): the account [selectedId] in the editor, the first without it, or a new one with [new]. */
    data class Accounts(val selectedId: Long? = null, val new: Boolean = false) : Route(
        when {
            new -> "$PATH/$NEW"
            selectedId != null -> "$PATH/$selectedId"
            else -> PATH
        },
    ) {
        companion object {
            const val PATH = "/konten"
            const val NEW = "neu"
        }
    }

    /** "Passwort ändern" for the signed-in account (#66). */
    data object ChangePassword : Route("/passwort")

    /** A one-time link (#66), with or without a session; the token is in the fragment, which routes ignore. */
    data object SetPassword : Route(SET_PASSWORD_PATH)

    data class NotFound(val requestedPath: String) : Route(requestedPath)

    companion object {
        private val NEWS_ITEM = Regex("^${News.PATH}/([1-9][0-9]{0,17})$")
        private val ACT = Regex("^${Acts.PATH}/([1-9][0-9]{0,17})$")
        private val PERSON = Regex("^${People.PATH}/([1-9][0-9]{0,17})$")
        private val BOARD_MEMBER = Regex("^${Board.PATH}/([1-9][0-9]{0,17})$")
        private val SPONSOR = Regex("^${Sponsors.PATH}/([1-9][0-9]{0,17})$")
        private val ACCOUNT = Regex("^${Accounts.PATH}/([1-9][0-9]{0,17})$")

        fun parse(path: String): Route {
            val trimmed = path.trimEnd('/').ifEmpty { "/" }
            NEWS_ITEM.matchEntire(trimmed)?.let { return News(it.groupValues[1].toLong()) }
            ACT.matchEntire(trimmed)?.let { return Acts(it.groupValues[1].toLong()) }
            PERSON.matchEntire(trimmed)?.let { return People(it.groupValues[1].toLong()) }
            BOARD_MEMBER.matchEntire(trimmed)?.let { return Board(it.groupValues[1].toLong()) }
            ACCOUNT.matchEntire(trimmed)?.let { return Accounts(it.groupValues[1].toLong()) }
            SPONSOR.matchEntire(trimmed)?.let { return Sponsors(it.groupValues[1].toLong()) }

            return when (trimmed) {
                Overview.path -> Overview
                SignIn.PATH -> SignIn()
                News.PATH -> News()
                "${News.PATH}/${News.NEW}" -> News(new = true)
                Acts.PATH -> Acts()
                "${Acts.PATH}/${Acts.NEW}" -> Acts(new = true)
                People.PATH -> People()
                "${People.PATH}/${People.NEW}" -> People(new = true)
                Board.PATH -> Board()
                "${Board.PATH}/${Board.NEW}" -> Board(new = true)
                Sponsors.PATH -> Sponsors()
                "${Sponsors.PATH}/${Sponsors.NEW}" -> Sponsors(new = true)
                Club.path -> Club
                Imprint.path -> Imprint
                Connections.path -> Connections
                Accounts.PATH -> Accounts()
                "${Accounts.PATH}/${Accounts.NEW}" -> Accounts(new = true)
                ChangePassword.path -> ChangePassword
                SetPassword.path -> SetPassword
                else -> NotFound(path)
            }
        }
    }
}

/** Keeps [current] in step with the History API. */
class Router(initial: Route) {
    var current: Route by mutableStateOf(initial)
        private set

    /** Asked before the browser's back and forward buttons drop unsaved changes. */
    var guard: UnsavedChangesGuard? = null

    /** The position of the current entry in the session history; each entry the router writes carries its own. */
    private var index = 0

    /** The next `popstate` undoes a traversal while the person decides. */
    private var undoingTraversal = false

    /** The next `popstate` redoes a traversal the person confirmed. */
    private var resumingTraversal = false

    /** The paths of the entries seen in this page, by position; a reload forgets them. */
    private val paths = mutableMapOf<Int, String>()

    /** Whether going back reaches [route], as far as this page knows. */
    fun previousIs(route: Route): Boolean = paths[index - 1] == route.path

    /** One step back, for a caller that asked about unsaved changes already. */
    fun back() {
        resumingTraversal = true
        window.history.back()
    }

    fun navigate(route: Route, replace: Boolean = false) {
        if (route == current) {
            return
        }

        if (replace) {
            window.history.replaceState(entryState(index), "", route.path)
        } else {
            index++
            window.history.pushState(entryState(index), "", route.path)
        }
        paths[index] = route.path
        current = route
    }

    private fun onPopState(state: Any?) {
        val target = Route.parse(window.location.pathname)
        val targetIndex = state?.asDynamic()?.index as? Int
        val guard = guard

        when {
            undoingTraversal -> undoingTraversal = false

            // entries the router didn't write have no position; leaving the page asks through beforeunload
            resumingTraversal || guard == null || !guard.hasChanges || targetIndex == null -> {
                resumingTraversal = false
                targetIndex?.let {
                    index = it
                    paths[it] = target.path
                }
                current = target
            }

            else -> {
                // back, forward or several steps at once: undo the step while the person decides, redo it on confirming
                val steps = targetIndex - index
                undoingTraversal = true
                window.history.go(-steps)
                guard.proceed {
                    resumingTraversal = true
                    window.history.go(steps)
                }
            }
        }
    }

    companion object {
        private fun entryState(index: Int): Any = kotlin.js.json("index" to index)

        fun forWindow(): Router = Router(Route.parse(window.location.pathname)).also { router ->
            // a reload keeps the entry's position
            router.index = window.history.state?.asDynamic()?.index as? Int ?: 0
            router.paths[router.index] = router.current.path
            // keeps the fragment, which carries the token of a one-time link (#66)
            window.history.replaceState(entryState(router.index), "", window.location.pathname + window.location.hash)
            window.addEventListener("popstate", { event -> router.onPopState((event as PopStateEvent).state) })
        }
    }
}
